import { getActivePreviewCompany } from './flag';
import { previewData } from './data';

// Preview Mode emulation of the refund UI (D-210): the two 0139 read RPCs and the admin-invite
// `execute_refund` action. Enough of the observable contract for UI verification — the limit check,
// the override justification rule, the draft-bound idempotency replay, the newest-first history —
// not a re-implementation of fn_execute_refund. In-memory only; nothing is ever sent anywhere.

type Row = Record<string, unknown>;
type Result<T> = { data: T; error: null | { message: string } };

/** Every Preview customer starts with this much refundable advance (demo value). */
const DEMO_ADVANCE_TETRI = 125_000n;
const AMOUNT_RE = /^\d{1,14}(\.\d{1,2})?$/;

interface PreviewRefund {
  readonly id: string;
  readonly companyId: string;
  readonly createdAt: string;
  readonly customerId: string;
  readonly amountTetri: bigint;
  readonly body: Row;
}

const refunds: PreviewRefund[] = [];
const replays = new Map<string, { hash: string; result: Row }>();

function toTetri(v: string): bigint {
  const [whole = '0', frac = ''] = v.split('.');
  return BigInt(whole) * 100n + BigInt(frac.padEnd(2, '0').slice(0, 2));
}
function fromTetri(t: bigint): string {
  const sign = t < 0n ? '-' : '';
  const abs = t < 0n ? -t : t;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}00`;
}
function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function availableTetri(companyId: string, customerId: string): bigint {
  const spent = refunds
    .filter((r) => r.companyId === companyId && r.customerId === customerId)
    .reduce((sum, r) => sum + r.amountTetri, 0n);
  const left = DEMO_ADVANCE_TETRI - spent;
  return left > 0n ? left : 0n;
}

function customerRow(companyId: string, customerId: string): Row | undefined {
  return (previewData(companyId).tables['customers'] as Row[] | undefined)?.find((c) => c['id'] === customerId);
}

/** The two 0139 read RPCs; `undefined` for any other function (the previewPartyRpc convention). */
export function previewRefundRpc(fn: string, args: Row): Result<unknown> | undefined {
  const companyId = getActivePreviewCompany();
  if (fn === 'fn_refund_available_advance') {
    const customerId = text(args['p_customer_id']);
    if (customerId === '' || customerRow(companyId, customerId) === undefined) {
      return { data: null, error: { message: 'REFUND_CUSTOMER_NOT_FOUND' } };
    }
    return {
      data: { ok: true, customer_id: customerId, currency: 'GEL', available_advance: fromTetri(availableTetri(companyId, customerId)) },
      error: null,
    };
  }
  if (fn === 'fn_refund_list') {
    const customerId = text(args['p_customer_id']);
    const from = text(args['p_from']);
    const to = text(args['p_to']);
    if (from !== '' && to !== '' && from > to) return { data: null, error: { message: 'REFUND_LIST_INVALID_RANGE' } };
    const rows = refunds
      .filter((r) => r.companyId === companyId)
      .filter((r) => customerId === '' || r.customerId === customerId)
      .filter((r) => from === '' || r.createdAt.slice(0, 10) >= from)
      .filter((r) => to === '' || r.createdAt.slice(0, 10) <= to)
      .slice()
      .reverse()
      .map((r) => {
        const c = customerRow(companyId, r.customerId);
        const override = r.body['override'] === true;
        return {
          id: r.id,
          created_at: r.createdAt,
          customer_id: r.customerId,
          customer_name: c?.['name'] ?? null,
          customer_tax_id: c?.['tax_id'] ?? null,
          amount: fromTetri(r.amountTetri),
          recipient_name: text(r.body['recipient_name']),
          recipient_account: text(r.body['recipient_account_number']),
          recipient_bank_name: text(r.body['recipient_bank_name']) || null,
          override_requested: override,
          override_reason: override ? text(r.body['override_reason']) : null,
          override_comment: override ? text(r.body['override_comment']) : null,
          created_by_name: 'Preview მომხმარებელი',
        };
      });
    return { data: { ok: true, rows, truncated: false }, error: null };
  }
  return undefined;
}

/** admin-invite `execute_refund`, mirroring the handler's refusal codes and replay behaviour. */
export function previewExecuteRefund(companyId: string, body: Row): Result<unknown> {
  const customerId = text(body['customer_id']);
  const amount = typeof body['amount'] === 'string' ? body['amount'] : '';
  const key = text(body['idempotency_key']);
  if (customerRow(companyId, customerId) === undefined) return { data: null, error: { message: 'REFUND_CUSTOMER_NOT_FOUND' } };
  if (!AMOUNT_RE.test(amount) || !/[1-9]/.test(amount)) return { data: null, error: { message: 'REFUND_INVALID_AMOUNT' } };
  if (text(body['recipient_name']) === '') return { data: null, error: { message: 'REFUND_RECIPIENT_REQUIRED' } };
  if (text(body['recipient_account_number']) === '') return { data: null, error: { message: 'REFUND_ACCOUNT_REQUIRED' } };
  const override = body['override'] === true;
  if (override && (text(body['override_reason']) === '' || text(body['override_comment']) === '')) {
    return { data: null, error: { message: 'REFUND_OVERRIDE_REASON_REQUIRED' } };
  }
  if (key === '') return { data: null, error: { message: 'INVALID_IDEMPOTENCY_KEY' } };

  const opKey = `${companyId}:${key}`;
  const hash = JSON.stringify(body);
  const prior = replays.get(opKey);
  if (prior !== undefined) {
    return prior.hash === hash ? { data: prior.result, error: null } : { data: null, error: { message: 'IDEMPOTENCY_CONFLICT' } };
  }

  const amountTetri = toTetri(amount);
  if (amountTetri > availableTetri(companyId, customerId) && !override) {
    return { data: null, error: { message: 'REFUND_LIMIT_EXCEEDED' } };
  }
  const id = `rf-preview-${refunds.length + 1}`;
  refunds.push({ id, companyId, createdAt: new Date().toISOString(), customerId, amountTetri, body });
  const result = { ok: true, refund_id: id, override };
  replays.set(opKey, { hash, result });
  return { data: result, error: null };
}
